import { getSkinConfig } from "../application/skin-rules";
import { gameplayConfig, type SkinId } from "../config/gameplay-config";
import {
  createSnakeHeadOutline,
  projectSnakeHeadFeature,
} from "../config/snake-head-geometry";
import { getPlayerBodySegmentCount } from "../simulation/player-body-trail";

export type SnakeVisualPartKind = "body" | "head" | "neck" | "tail";

export interface SnakeVisualPart {
  readonly index: number;
  readonly kind: SnakeVisualPartKind;
  readonly patternOffsetPx: number;
  readonly taper: 0 | 1 | 2 | 3;
}

const svgNamespace = "http://www.w3.org/2000/svg";

const createSvgNode = <TagName extends keyof SVGElementTagNameMap>(
  documentScope: Document,
  tagName: TagName,
  attributes: Readonly<Record<string, string>>,
): SVGElementTagNameMap[TagName] => {
  const node = documentScope.createElementNS(svgNamespace, tagName);
  for (const [name, value] of Object.entries(attributes)) {
    node.setAttribute(name, value);
  }
  return node;
};

const createSnakeHeadVisual = (documentScope: Document): SVGSVGElement => {
  const radius = 30;
  const position = { x: 45, y: 33 };
  const direction = {
    forwardX: -1,
    forwardY: 0,
    sideX: 0,
    sideY: -1,
  } as const;
  const visual = gameplayConfig.snakeVisual;
  const outline = createSnakeHeadOutline(position, radius, direction, visual);
  const svg = createSvgNode(documentScope, "svg", {
    "aria-hidden": "true",
    class: "snakely-snake-head-visual",
    focusable: "false",
    viewBox: "0 0 78 66",
  });
  const surface = createSvgNode(documentScope, "polygon", {
    class: "snakely-snake-head-surface",
    points: outline.map(({ x, y }) => `${String(x)},${String(y)}`).join(" "),
  });
  const mouthCenter = projectSnakeHeadFeature(
    position,
    radius,
    direction,
    visual.mouthForwardScale,
    0,
  );
  const mouth = createSvgNode(documentScope, "line", {
    class: "snakely-snake-head-mouth",
    x1: String(mouthCenter.x),
    x2: String(mouthCenter.x),
    y1: String(mouthCenter.y - radius * 0.12),
    y2: String(mouthCenter.y + radius * 0.12),
  });
  const eyes = [-1, 1].flatMap((sideSign) => {
    const eye = projectSnakeHeadFeature(
      position,
      radius,
      direction,
      visual.eyeForwardScale,
      visual.eyeSideScale * sideSign,
    );
    const highlight = projectSnakeHeadFeature(
      eye,
      radius,
      direction,
      visual.eyeHighlightForwardScale,
      0,
    );
    return [
      createSvgNode(documentScope, "circle", {
        class: "snakely-snake-head-eye",
        cx: String(eye.x),
        cy: String(eye.y),
        r: String(radius * visual.eyeRadiusScale),
      }),
      createSvgNode(documentScope, "circle", {
        class: "snakely-snake-head-highlight",
        cx: String(highlight.x),
        cy: String(highlight.y),
        r: String(radius * visual.eyeHighlightRadiusScale),
      }),
    ];
  });
  svg.replaceChildren(surface, mouth, ...eyes);
  return svg;
};

const getPartKind = (
  index: number,
  finalIndex: number,
): SnakeVisualPartKind => {
  if (index === 0) {
    return "head";
  }
  if (index === 1) {
    return "neck";
  }
  return index === finalIndex ? "tail" : "body";
};

const getTaper = (index: number, finalIndex: number): 0 | 1 | 2 | 3 => {
  const taper = Math.max(0, 3 - (finalIndex - index));
  if (taper === 1 || taper === 2 || taper === 3) {
    return taper;
  }
  return 0;
};

export const getSnakeVisualParts = (
  level: number,
): readonly SnakeVisualPart[] => {
  const bodyCount = getPlayerBodySegmentCount(level, gameplayConfig.playerBody);
  const finalIndex = bodyCount;
  const anatomy = Array.from({ length: bodyCount + 1 }, (_, index) => ({
    index: index + 1,
    kind: getPartKind(index, finalIndex),
    patternOffsetPx: -index * gameplayConfig.snakeVisual.patternStepPx,
    taper: getTaper(index, finalIndex),
  }));
  return anatomy;
};

export const getDisplayedSnakeVisualParts = (
  parts: readonly SnakeVisualPart[],
): readonly SnakeVisualPart[] => {
  const maximum = gameplayConfig.snakeVisual.maximumUnscaledParts;
  if (parts.length <= maximum) {
    return parts;
  }
  const head = parts[0];
  const neck = parts[1];
  const tail = parts[parts.length - 1];
  if (head === undefined || neck === undefined || tail === undefined) {
    throw new Error("Snake visual anatomy is incomplete");
  }
  const body = parts.slice(2, -1);
  const bodySlots = maximum - 3;
  const selectedBody = Array.from({ length: bodySlots }, (_, index) => {
    const sourceIndex = Math.round(
      (index / (bodySlots - 1)) * (body.length - 1),
    );
    const part = body[sourceIndex];
    if (part === undefined) {
      throw new Error("Snake visual body sample is absent");
    }
    return part;
  });
  return [head, neck, ...selectedBody, tail];
};

export const renderSnakeVisual = (
  element: HTMLElement,
  skinId: SkinId,
  level: number,
  label?: string,
): void => {
  const skin = getSkinConfig(skinId);
  const visualParts = getDisplayedSnakeVisualParts(getSnakeVisualParts(level));
  const parts = visualParts.map((part) => {
    const node = element.ownerDocument.createElement("span");
    node.className = `snakely-snake-part snakely-snake-${part.kind} snakely-snake-taper-${String(part.taper)}`;
    node.dataset.part = part.kind;
    node.dataset.patternPhase = String(part.index % 4);
    node.style.setProperty(
      "--snake-pattern-offset",
      `${String(part.patternOffsetPx)}px`,
    );
    node.setAttribute("aria-hidden", "true");
    if (part.kind === "head") {
      node.replaceChildren(createSnakeHeadVisual(element.ownerDocument));
    }
    return node;
  });
  element.replaceChildren(...parts);
  element.dataset.anatomy = gameplayConfig.snakeVisual.anatomyId;
  element.dataset.level = String(level);
  element.dataset.pattern = skin.pattern;
  element.dataset.silhouette = "rounded-snake-head-taper";
  element.dataset.skinId = skin.id;
  element.style.setProperty("--snake-scale", "1");
  if (label === undefined) {
    element.removeAttribute("aria-label");
  } else {
    element.setAttribute("aria-label", label);
  }
};
